import { useQuery } from '@tanstack/react-query';
import { Select } from 'antd';
import { cn } from 'cn';
import { DEBOUNCE_TIME } from '@/std/const/debounceTime';
import { filterOption } from '@/std/filter';
import { useDebouncedState } from '@/std/hooks/useDebouncedState';
import {
    type ApiRegraFiscalGetParams,
    apiRegraFiscalGetQueryOptions,
} from '../api/apiRegraFiscalGet';
import { RegraFiscalTipo, type RegraFiscal } from '../types/RegraFiscal';

export function SelectRegraFiscal({
    showSemRegraFiscal,
    disabled,
    value,
    onChange,
    className,
    tipo,
}: {
    showSemRegraFiscal?: boolean;
    className?: string;
    disabled?: boolean;
    value?: number | null;
    onChange?: (idpk: number | null, regra: RegraFiscal | null) => void;
    tipo?: RegraFiscalTipo | null;
}) {
    const [search, setSearch] = useDebouncedState('', DEBOUNCE_TIME);

    const params: ApiRegraFiscalGetParams = {
        descricao: search,
        orderby: 'ref_descricao',
        where: tipo ? `ref_tipo=${tipo}` : undefined,
    };

    const { data, isFetching } = useQuery(apiRegraFiscalGetQueryOptions({ params }));

    const options: { value: number; label: string }[] = showSemRegraFiscal
        ? [
              {
                  label: 'Sem grupo fiscal',
                  value: -1,
              },
          ]
        : [];

    if (data?.registros) {
        for (const regra of data.registros) {
            options.push({
                label: regra.ref_descricao ?? '',
                value: regra.ref_idpk,
            });
        }
    }

    const handleChange = (newValue: number | undefined | null): void => {
        if (!(data && onChange)) {
            return;
        }

        if (newValue === -1) {
            onChange(-1, {
                ref_descricao: 'Sem grupo fiscal',
                ref_empresa_idpk: 0,
                ref_idpk: -1,
                ref_nfe_altera_regra: 'N',
                ref_tipo: tipo ?? RegraFiscalTipo.Produto,
                ref_ultima_alteracao: null,
                regra_fiscal_cofins: null,
                regra_fiscal_ibscbs: null,
                regra_fiscal_icms: null,
                regra_fiscal_informacoes: null,
                regra_fiscal_ipi: null,
                regra_fiscal_is: null,
                regra_fiscal_pis: null,
                regra_fiscal_servico: null,
            });

            return;
        }

        const regra = data.registros.find((r) => r.ref_idpk === newValue);
        onChange(newValue || null, regra || null);
    };

    return (
        <Select
            allowClear={true}
            className={cn('w-full', className)}
            disabled={disabled}
            loading={isFetching}
            notFoundContent={isFetching ? 'Pesquisando...' : undefined}
            onChange={handleChange}
            options={options}
            placeholder='Selecione um grupo fiscal'
            showSearch={{
                filterOption,
                onSearch: setSearch,
            }}
            value={value}
        />
    );
}
